<script setup lang="ts">
import type { ButtonProps } from '#ui/types';

import { ROUTE_NAMES } from '~/constants/routes';
import { isAuction, type PlatformSaleItemEntity } from '~/types/sale.type';

// PROPS
interface Props {
    sale: PlatformSaleItemEntity;
}

const { sale } = defineProps<Props>();

// COMPOSABLES
const { tt } = useTypedI18n();
const localePath = useLocalePath();
const router = useRouter();
const { title } = useSaleTitle(() => sale);
const { frontendBaseUrl } = useRuntimeConfig().public;

// DATA
const source = computed(() => {
    const path = ((): string => {
        // The smart link is excluded from localization (`i18nCustomPages`) and since @nuxtjs/i18n 10.3.0
        // localePath() returns '' for any route without a localized variant, so the router resolves it.
        if (!sale.hiddenFromListing)
            return router.resolve({ name: ROUTE_NAMES.SMART_LINK_VEHICLE_CODE, params: { vehicleCode: sale.mainAsset.vehicleCode } }).fullPath;

        return localePath({ name: isAuction(sale) ? ROUTE_NAMES.AUCTION_DETAIL : ROUTE_NAMES.DIRECT_SALE_DETAIL, params: { slug: sale.slug } });
    })();

    return `${frontendBaseUrl}${path}`;
});

const shareSource = computed(() => ({
    title: title.value,
    url: source.value,
}));

const buttonProps: Pick<ButtonProps, 'color' | 'variant'> = { color: 'primary', variant: 'soft' };

const { share, isSupported: isSupportedShare } = useShare(shareSource);
const { copy, copied, isSupported: isSupportedClipboard } = useClipboard({ source });

// HANDLERS
const onClickShare = (): Promise<void> => share();

const onClickClipboard = (): Promise<void> => copy(source.value);
</script>

<template>
    <UButton v-if="isSupportedShare" icon="lucide:share" :label="tt('sdp.share.label')" v-bind="buttonProps" @click="onClickShare" />
    <UButton v-else :disabled="!isSupportedClipboard" icon="lucide:copy" v-bind="buttonProps" @click="onClickClipboard">
        <span v-if="!copied">{{ tt('sdp.share.label') }}</span>
        <span v-else>{{ tt('sdp.share.copied') }}</span>
    </UButton>
</template>
